import { inquiryApi } from '../api/inquiryApi';
import type { InquiryItem, InquiryDetail, InquirySummary, InquiryFilterParams } from '../types/inquiry';
import { useBaseQuery, useBaseMutation, useNoDataMutation, useInvalidateQueries } from './useBaseQuery';
import { PaginationData } from '../models/paginationData';

export const inquiryKeys = {
    all: ['inquiry'] as const,
    lists: () => [...inquiryKeys.all, 'list'] as const,
    list: (params: InquiryFilterParams) => [...inquiryKeys.lists(), params] as const,
    summary: () => [...inquiryKeys.all, 'summary'] as const,
    detail: (id: number) => [...inquiryKeys.all, 'detail', id] as const,
};

export const useInquiries = (params: InquiryFilterParams) => {
    return useBaseQuery<PaginationData<InquiryItem>>(
        inquiryKeys.list(params),
        () => inquiryApi.getInquiries(params),
        { staleTime: 10_000 },
    );
};

export const useInquirySummary = () => {
    return useBaseQuery<InquirySummary>(
        inquiryKeys.summary(),
        () => inquiryApi.getSummary(),
        { staleTime: 10_000 },
    );
};

export const useInquiryDetail = (id: number | null) => {
    return useBaseQuery<InquiryDetail>(
        inquiryKeys.detail(id ?? 0),
        () => inquiryApi.getDetail(id!),
        { enabled: id != null, staleTime: 5_000 },
    );
};

export const useAnswerInquiry = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useBaseMutation<{ id: number; answer: string }, InquiryDetail>(
        ({ id, answer }) => inquiryApi.answer(id, answer),
        {
            onSuccess: (_, { id }) => {
                invalidateMultiple([inquiryKeys.lists(), inquiryKeys.summary(), inquiryKeys.detail(id)]);
            },
        },
    );
};

export const useChangeInquiryStatus = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useNoDataMutation<{ id: number; status: string }>(
        ({ id, status }) => inquiryApi.changeStatus(id, status),
        {
            onSuccess: () => {
                invalidateMultiple([inquiryKeys.lists(), inquiryKeys.summary()]);
            },
        },
    );
};
